<template>
  <DefaultLayout>
    <div class="not-found-page">
      <div class="container mx-auto px-6 py-16">
        <div class="max-w-4xl mx-auto">
          
          <!-- Main Error Section -->
          <div class="text-center mb-12">
            <!-- Error Icon -->
            <div class="error-icon-container mb-8">
              <div class="error-icon">
                <Icon icon="mdi:shield-alert" width="120" height="120" />
              </div>
            </div>
            
            <!-- Error Code -->
            <h1 class="error-code heading-1">404</h1>
            
            <!-- Error Message -->
            <h2 class="error-title heading-2">Page Not Found</h2>
            
            <!-- Error Description -->
            <p class="error-description body-large">
              We're sorry, but the page you were looking for doesn't exist. 
              It might have been moved, deleted, or you may have mistyped the URL.
            </p>
          </div>

          <!-- Search Section -->
          <div class="search-section mb-12">
            <div class="max-w-md mx-auto">
              <h3 class="heading-3 text-center mb-6">Try searching for what you need:</h3>
              <div class="search-container">
                <div class="search-input-wrapper">
                  <Icon icon="mdi:magnify" class="search-icon" />
                  <input 
                    type="text" 
                    v-model="searchQuery"
                    @keyup.enter="performSearch"
                    placeholder="Search for insurance products, services..."
                    class="search-input"
                  >
                </div>
                <button @click="performSearch" class="search-button">
                  Search
                </button>
              </div>
            </div>
          </div>

          <!-- Quick Links Section -->
          <div class="quick-links-section">
            <h3 class="heading-3 text-center mb-8">Popular Pages</h3>
            <div class="quick-links-grid">
              
              <!-- Get Quote Card -->
              <router-link to="/chooseproducts" class="quick-link-card">
                <div class="card-icon">
                  <Icon icon="mdi:calculator" width="32" height="32" />
                </div>
                <h4 class="card-title">Get a Quote</h4>
                <p class="card-description">Compare insurance rates and get your personalized quote</p>
                <div class="card-arrow">
                  <Icon icon="mdi:arrow-right" width="20" height="20" />
                </div>
              </router-link>

              <!-- Customer Service Card -->
              <router-link to="/customerservice" class="quick-link-card">
                <div class="card-icon">
                  <Icon icon="mdi:headset" width="32" height="32" />
                </div>
                <h4 class="card-title">Customer Service</h4>
                <p class="card-description">Manage your policies, file claims, and get support</p>
                <div class="card-arrow">
                  <Icon icon="mdi:arrow-right" width="20" height="20" />
                </div>
              </router-link>

              <!-- About Us Card -->
              <router-link to="/aboutus" class="quick-link-card">
                <div class="card-icon">
                  <Icon icon="mdi:information" width="32" height="32" />
                </div>
                <h4 class="card-title">About Us</h4>
                <p class="card-description">Learn about our company and insurance expertise</p>
                <div class="card-arrow">
                  <Icon icon="mdi:arrow-right" width="20" height="20" />
                </div>
              </router-link>

              <!-- Products Card -->
              <router-link to="/products/homeinsurance" class="quick-link-card">
                <div class="card-icon">
                  <Icon icon="mdi:shield-check" width="32" height="32" />
                </div>
                <h4 class="card-title">Our Products</h4>
                <p class="card-description">Explore our comprehensive insurance coverage options</p>
                <div class="card-arrow">
                  <Icon icon="mdi:arrow-right" width="20" height="20" />
                </div>
              </router-link>

            </div>
          </div>

          <!-- Contact Section -->
          <div class="contact-section">
            <div class="contact-card">
              <h3 class="heading-3 text-center mb-6">Need Help? Contact Us</h3>
              <div class="contact-grid">
                <div class="contact-item">
                  <Icon icon="mdi:phone" class="contact-icon" />
                  <div>
                    <p class="contact-label">Call Us</p>
                    <a href="tel:800-268-9137" class="contact-value">800-268-9137</a>
                    <p class="contact-note">Mon-Fri 8AM-6PM EST</p>
                  </div>
                </div>
                <div class="contact-item">
                  <Icon icon="mdi:email" class="contact-icon" />
                  <div>
                    <p class="contact-label">Email Us</p>
                    <a href="mailto:customerservice@insuranceexpress.com" class="contact-value">customerservice@insuranceexpress.com</a>
                    <p class="contact-note">We'll respond within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Home Button -->
          <div class="home-button-section">
            <router-link to="/" class="home-button">
              <Icon icon="mdi:home" class="mr-2" />
              Back to Home
            </router-link>
          </div>

        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import { Icon } from '@iconify/vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';

export default {
  name: 'NotFoundPage',
  components: {
    Icon,
    DefaultLayout
  },
  data() {
    return {
      searchQuery: ''
    };
  },
  methods: {
    performSearch() {
      if (this.searchQuery.trim()) {
        // You can implement search functionality here
        // For now, redirect to home with search query
        this.$router.push({ 
          path: '/', 
          query: { search: this.searchQuery.trim() } 
        });
      }
    }
  },
  metaInfo: {
    title: '404 - Page Not Found | Insurance Express'
  }
}
</script>

<style scoped>
.not-found-page {
  background: linear-gradient(135deg, var(--color-bg-light) 0%, var(--color-bg-subtle) 100%);
  min-height: calc(100vh - 200px);
  padding: 2rem 0;
}

/* Error Icon */
.error-icon-container {
  position: relative;
}

.error-icon {
  color: var(--color-primary);
  opacity: 0.8;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

/* Error Text */
.error-code {
  font-size: 8rem;
  color: var(--color-primary);
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  line-height: 1;
}

.error-title {
  color: var(--color-text-dark);
  margin-bottom: 1.5rem;
}

.error-description {
  max-width: 600px;
  margin: 0 auto;
  color: var(--color-text-light);
}

/* Search Section */
.search-section {
  background-color: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--color-border);
}

.search-container {
  display: flex;
  gap: 0.75rem;
  flex-direction: column;
}

@media (min-width: 640px) {
  .search-container {
    flex-direction: row;
  }
}

.search-input-wrapper {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  width: 20px;
  height: 20px;
}

.search-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid var(--color-border);
  border-radius: 0.5rem;
  font-family: var(--font-sans);
  font-size: 1rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(219, 9, 8, 0.1);
}

.search-button {
  background-color: var(--color-primary);
  color: white;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 0.5rem;
  font-family: var(--font-sans);
  font-weight: var(--font-medium);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.search-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

/* Quick Links */
.quick-links-section {
  margin-bottom: 3rem;
}

.quick-links-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .quick-links-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .quick-links-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.quick-link-card {
  background-color: white;
  border: 1px solid var(--color-border);
  border-radius: 0.75rem;
  padding: 2rem;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 200px;
}

.quick-link-card:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-primary-light));
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.quick-link-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: var(--color-primary);
}

.quick-link-card:hover:before {
  transform: scaleX(1);
}

.card-icon {
  color: var(--color-primary);
  margin-bottom: 1rem;
  transition: transform 0.3s ease;
}

.quick-link-card:hover .card-icon {
  transform: scale(1.1);
}

.card-title {
  font-family: var(--font-sans);
  font-size: 1.125rem;
  font-weight: var(--font-bold);
  color: var(--color-text-dark);
  margin-bottom: 0.75rem;
}

.card-description {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-text-light);
  line-height: 1.6;
  flex: 1;
  margin-bottom: 1rem;
}

.card-arrow {
  color: var(--color-primary);
  align-self: flex-end;
  transition: transform 0.3s ease;
}

.quick-link-card:hover .card-arrow {
  transform: translateX(4px);
}

/* Contact Section */
.contact-section {
  margin-bottom: 3rem;
}

.contact-card {
  background-color: white;
  border-radius: 1rem;
  padding: 2.5rem;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--color-border);
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .contact-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  text-align: left;
}

.contact-icon {
  color: var(--color-primary);
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.contact-label {
  font-family: var(--font-sans);
  font-size: 0.875rem;
  font-weight: var(--font-medium);
  color: var(--color-text-muted);
  margin-bottom: 0.25rem;
}

.contact-value {
  font-family: var(--font-sans);
  font-size: 1.125rem;
  font-weight: var(--font-bold);
  color: var(--color-text-dark);
  text-decoration: none;
  transition: color 0.2s ease;
  display: block;
  margin-bottom: 0.25rem;
}

.contact-value:hover {
  color: var(--color-primary);
}

.contact-note {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0;
}

/* Home Button */
.home-button-section {
  text-align: center;
}

.home-button {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  color: white;
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  font-family: var(--font-sans);
  font-weight: var(--font-bold);
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(219, 9, 8, 0.2);
}

.home-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(219, 9, 8, 0.3);
  text-decoration: none;
  color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .error-code {
    font-size: 6rem;
  }
  
  .search-section,
  .contact-card {
    padding: 1.5rem;
  }
  
  .quick-link-card {
    padding: 1.5rem;
    min-height: 160px;
  }
  
  .contact-item {
    text-align: center;
    flex-direction: column;
    align-items: center;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .error-icon,
  .quick-link-card,
  .card-icon,
  .card-arrow,
  .home-button {
    animation: none;
    transition: none;
  }
  
  .quick-link-card:hover,
  .home-button:hover {
    transform: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .quick-link-card,
  .search-section,
  .contact-card {
    border: 2px solid var(--color-text-dark);
  }
  
  .search-input:focus {
    border: 3px solid var(--color-primary);
  }
}
</style> 